import React from 'react';
import { Box, FormControl, Select, Typography } from '@mui/material';
import {
  getMitsubishiAirDirections,
  louvreSettingLabel,
  MitsubishiPowerSnapshot,
} from '../lib/mitsubishi-power-edit';

interface Props {
  snapshot: Pick<
    MitsubishiPowerSnapshot,
    | 'airDirection'
    | 'supportsAirDirection'
    | 'canEditAirDirection'
    | 'allowedAirDirections'
    | 'powerMode'
    | 'operatingMode'
    | 'fanSpeed'
  >;
  editing?: boolean;
  value?: string | null;
  disabled?: boolean;
  onChange: (value: string) => void;
}

/** A schematic of the reported selection, never an animation claiming measured movement. */
export function LouvrePanel({
  snapshot,
  editing = false,
  value,
  disabled = false,
  onChange,
}: Props) {
  const choices = getMitsubishiAirDirections(snapshot);
  const reported = snapshot.airDirection?.value;
  const awaitingData =
    !snapshot.supportsAirDirection && !reported && choices.length === 0;
  const label = louvreSettingLabel(reported, choices);
  const pending = snapshot.airDirection?.pending === true;
  const writable =
    snapshot.canEditAirDirection === true &&
    choices.length > 0 &&
    !disabled &&
    !pending &&
    !snapshot.powerMode?.pending &&
    !snapshot.operatingMode?.pending &&
    !snapshot.fanSpeed?.pending;
  const angle = (
    { Horizontal: 0, Mid0: 22.5, Mid1: 45, Mid2: 67.5, Vertical: 90 } as Record<
      string,
      number
    >
  )[reported ?? ''];
  return (
    <Box
      component="section"
      aria-label="Louvre"
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 1.25,
        py: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          component="svg"
          viewBox="0 0 76 66"
          role="img"
          aria-label={`Louvre schematic: ${label}`}
          sx={{ width: 40, height: 36, flexShrink: 0, color: 'text.secondary' }}
        >
          <rect
            x="3"
            y="4"
            width="65"
            height="10"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          {angle !== undefined ? (
            <g transform={`rotate(${angle} 12 24)`}>
              <path
                d="M12 24 H48"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <path
                d="M18 31 H46 M42 27 L46 31 L42 35"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </g>
          ) : reported === 'Swing' ? (
            <>
              <path
                d="M12 24 Q51 24 51 56 M12 24 L19 19 M12 24 L19 29 M51 56 L46 49 M51 56 L56 49"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </>
          ) : (
            <text
              x="35"
              y="49"
              textAnchor="middle"
              fill="currentColor"
              fontSize="22"
            >
              {reported === 'Auto' ? 'A' : '?'}
            </text>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">Louvre</Typography>
          <Typography variant="body2" aria-live="polite">
            {awaitingData ? 'Not reported' : `Reported setting: ${label}`}
          </Typography>
          {awaitingData && (
            <Typography variant="caption" color="text.secondary">
              Awaiting louvre data from Conductor.
            </Typography>
          )}
          {pending && (
            <Typography variant="caption" role="status">
              Louvre change pending confirmation.
            </Typography>
          )}
          {reported === 'Auto' && (
            <Typography variant="caption" color="text.secondary">
              Position controlled automatically.
            </Typography>
          )}
          {reported === 'Swing' && (
            <Typography variant="caption" color="text.secondary">
              Swing selected.
            </Typography>
          )}
          {editing && !awaitingData && (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <Select
                native
                value={value ?? ''}
                disabled={!writable}
                inputProps={{ 'aria-label': 'Louvre setting' }}
                onChange={(event) => {
                  const next = event.target.value;
                  if (
                    writable &&
                    typeof next === 'string' &&
                    choices.includes(next)
                  )
                    onChange(next);
                }}
              >
                {!choices.includes(value ?? '') && (
                  <option value={value ?? ''} disabled>
                    {louvreSettingLabel(value, choices)}
                  </option>
                )}
                {choices.map((choice) => (
                  <option key={choice} value={choice}>
                    {louvreSettingLabel(choice, choices)}
                  </option>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary">
                {writable
                  ? 'Choose a setting, then Save Changes.'
                  : pending
                    ? 'Waiting for controller confirmation.'
                    : 'Louvre control is currently unavailable.'}
              </Typography>
            </FormControl>
          )}
        </Box>
      </Box>
    </Box>
  );
}
